import React, { useEffect, useRef } from 'react';
import { Link } from 'gatsby';
import { useStaticQuery, graphql } from 'gatsby';

// Helpers
import uniqBy from 'lodash/uniqBy';
import { formatCastMember } from '../../utils/helpers';
const { makeSearchFriendly } = require('../../../node-utils/stopwords');

// Components
import Poster from '../Poster';
import NetworkCardLink from '../NetworkCardLink';

// Models
import { IMedia, ICrewMember, INetwork } from '../../utils/models';

// Utils
import {
  excludedNetworkPaths,
  excludedNetworkNames,
} from '../../utils/excludedNetworksMap';

import './SearchResultsPanel.scss';

// TEALIUM
import initTealiumTracker from 'tealium-tracker';
import useSitePages from '../../hooks/useSitePages/useSitePages';
let tealiumTrackEvent;
if (typeof window !== 'undefined') {
  const { trackEvent } = initTealiumTracker({ debugMode: true });
  tealiumTrackEvent = trackEvent;
}

export interface ISearchResultsPanelProps {
  searchTerm: string;
  hits: (IMedia & INetwork)[];
  analytics: any;
}

const SearchResultsSection = ({ title, count, children }) => {
  return count ? (
    <section className="search-results-panel__section" aria-label={title}>
      <h3 className="search-results-panel__title">
        {title} ({count})
      </h3>
      <ul
        className={`search-results-panel__list search-results-panel__list--${title}`}
      >
        {children}
      </ul>
    </section>
  ) : null;
};

const SearchResultsPanel = ({
  searchTerm,
  hits,
  analytics,
}: ISearchResultsPanelProps) => {
  // Queries the "Max" logo from the cms
  const data = useStaticQuery(graphql`
    query {
      allStrapi {
        offers(filters: { slug: { eq: "max" } }) {
          logo {
            url
          }
        }
      }
    }
  `);

  const { sitePageMap } = useSitePages();

  const maxLogoUrl = data?.allStrapi?.offers?.[0]?.logo?.url;

  // Titles
  const termRegex = new RegExp(makeSearchFriendly(searchTerm), 'gi');

  // Mike Code, dont think this is needed
  // const titles = hits.filter((result: any) => {
  //   if (result.formattedTitle && result.assetType !== 'Event') {
  //     const matches = result.formattedTitle.match(termRegex);
  //     console.log(`matches`, matches);
  //     return matches?.length;
  //   }
  // });

  const titles = hits
    .filter((result: any) => {
      const hasAssetType = result.assetType;
      const pageExists = sitePageMap.has(result.id);
      const hasPath = sitePageMap.get(result.id) || result.path;
      const isExcludedNetwork = excludedNetworkPaths.some((networkPath) =>
        result.path.includes(networkPath)
      );

      return hasAssetType && pageExists && hasPath && !isExcludedNetwork;
    })
    .map((result: any) => {
      // replace path from search result with the one from the Gatsby app page map
      // this is needed because the search result paths do not always directly correspond to the Gatsby page paths
      const pagePath = sitePageMap.get(result.id);
      if (pagePath) {
        result.path = pagePath;
      }
      return result;
    });

  // Cast
  const cast = uniqBy(
    hits.reduce((acc: ICrewMember[], result: any) => {
      if (result.topCast) {
        const foundMember = result.topCast.find((member: ICrewMember) => {
          const matches = makeSearchFriendly(member.name).match(termRegex);
          return matches?.length;
        });
        foundMember && acc.push(foundMember);
      }
      return acc;
    }, []),
    'personId'
  );

  // Networks
  const networks = hits.filter((result: any) => {
    const isExcludedNetwork = excludedNetworkNames.includes(result.displayName);
    return result.displayName && !isExcludedNetwork;
  });

  // Events
  const events = hits.filter((result: any) => {
    if (result.assetType === 'Event') {
      const matches = result.formattedTitle.match(termRegex);
      return matches?.length;
    }
  });

  const totalResultsCount =
    titles.length + cast.length + networks.length + events.length;

  const searchResultsPanelRef = useRef<HTMLDivElement | null>(null);

  const trackedSearchResults = [...titles, ...cast, ...networks, ...events]
    .slice(0, 10)
    .map((result) => {
      if (
        'assetType' in result &&
        (result?.assetType === 'Series' ||
          result?.assetType === 'Movie' ||
          result?.assetType === 'Event')
      ) {
        return {
          id: result?.id,
          idType: 'asset id',
          facet: result?.assetType,
          url: result?.path,
          name: result?.title,
        };
      } else if ('name' in result) {
        return {
          id: result?.personId,
          idType: 'asset id',
          facet: 'person',
          url: '',
          name: result?.name,
        };
      } else if ('displayName' in result) {
        return {
          id: result?.displayName,
          idType: 'asset id',
          facet: 'network',
          url: result?.path,
          name: result?.displayName,
        };
      }

      return {
        id: '',
        idType: '',
        facet: '',
        url: '',
        name: '',
      };
    });

  // Analytics
  useEffect(() => {
    // Quantum
    analytics.track('searched', {
      msgTriggeredBy: 'user',
      searchElem: totalResultsCount,
      searchId: searchTerm,
      searchType: 'keyword',
      searchResultsMs: 500,
      searchText: searchTerm,
      searchResults: trackedSearchResults,
    });

    // Tealium
    tealiumTrackEvent({
      searchKeyword: searchTerm,
      'qp.q': searchTerm,
      section: 'Search',
      pagename: 'Search Page',
      pagetype: 'Search Results',
      numResults: totalResultsCount,
    });
  }, [analytics, totalResultsCount, searchTerm]);

  return (
    <div
      className="search-results-panel"
      ref={searchResultsPanelRef}
      tabIndex={-1}
    >
      <h2 className="search-results-panel__count">
        Search Results ({totalResultsCount})
      </h2>
      <section className="search-results-panel__body" aria-label="Results">
        {/* Events */}
        <SearchResultsSection title="Events" count={events.length}>
          {events.map((event) => {
            const { title, path = '/' } = event;
            return (
              <li key={event.title}>
                <Link to={path} aria-label={title}>
                  <Poster media={event} isEvent={true} />
                </Link>
              </li>
            );
          })}
        </SearchResultsSection>

        {/* NETWORKS */}
        <SearchResultsSection title="Networks" count={networks.length}>
          {networks.map((network) => {
            const { displayName, path = '/' } = network;

            return (
              <li
                key={displayName}
                className={
                  displayName === 'Max' ||
                  displayName === 'HBO Max' ||
                  displayName === 'HBO'
                    ? 'search-results-panel__list--max'
                    : ''
                }
              >
                <NetworkCardLink
                  network={network}
                  linkPath={path}
                  maxLogoUrl={maxLogoUrl}
                  ariaLabel={`${displayName}`}
                />
              </li>
            );
          })}
        </SearchResultsSection>

        {/* CAST */}
        <SearchResultsSection title="Cast" count={cast.length}>
          {cast.map((member: any) => {
            const { name, personId } = formatCastMember(member);
            return (
              <li key={personId || name}>
                <Link
                  aria-label={`View titles with ${member.name}`}
                  to={`/cast/?name=${name}`}
                >
                  <Poster media={member} />
                </Link>
              </li>
            );
          })}
        </SearchResultsSection>

        {/* TITLES */}
        <SearchResultsSection title="Titles" count={titles.length}>
          {titles.map((result) => {
            const { title, path = '/', id } = result;
            return (
              <li key={id}>
                <Link to={path} aria-label={`${title}`}>
                  <Poster media={result} />
                </Link>
              </li>
            );
          })}
        </SearchResultsSection>
      </section>
    </div>
  );
};

export default SearchResultsPanel;
